import React, { useState, useMemo } from 'react';
import { FiCheck, FiX, FiAlertCircle, FiClock, FiUser, FiLock, FiTruck, FiDollarSign, FiMapPin, FiCheckCircle } from 'react-icons/fi';
import { useSelector } from 'react-redux';

// Get PST date string
function getPSTDateString(date = new Date()) {
  const pstDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const year = pstDate.getFullYear();
  const month = String(pstDate.getMonth() + 1).padStart(2, '0');
  const day = String(pstDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getOrderDateInPST(inStockDate) {
  if (!inStockDate) return null;
  return getPSTDateString(new Date(inStockDate));
}

export default function EODReviewMode({ 
  orders, 
  completedOrders,
  onApproveOrder,
  onClose 
}) {
  const { adminInfo } = useSelector(s => s.login);
  const reviewerName = adminInfo ? `${adminInfo.firstName || ''} ${adminInfo.lastName || ''}`.trim() : 'Unknown';
  
  // Get tomorrow's date
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrowStr = getPSTDateString(tomorrowDate);
  
  // Filter orders for tomorrow
  const tomorrowOrders = useMemo(() => {
    const allOrders = [...orders, ...completedOrders];
    return allOrders.filter(o => getOrderDateInPST(o.inStockDate) === tomorrowStr);
  }, [orders, completedOrders, tomorrowStr]);

  // Track local verification state for each order
  const [verificationState, setVerificationState] = useState({});
  
  // Get verification state for an order
  const getOrderVerification = (orderId) => {
    return verificationState[orderId] || {
      pickupTimeVerified: false,
      entryVerified: false,
      deliveryTimeVerified: false,
      driverVerified: false,
      storeOrderVerified: false,
      storeSpendVerified: false,
      driverPayVerified: false,
    };
  };

  // Toggle a verification checkbox
  const toggleVerification = (orderId, field) => {
    setVerificationState(prev => ({
      ...prev,
      [orderId]: {
        ...getOrderVerification(orderId),
        [field]: !getOrderVerification(orderId)[field]
      }
    }));
  };

  // Check if all verifications are complete for an order
  const isOrderFullyVerified = (order) => {
    const v = getOrderVerification(order._id);
    const hasDriver = !!order.driverID;
    const hasDriverPay = parseFloat(order.driverPay) > 0;
    
    // Required checks
    const requiredChecks = [
      v.pickupTimeVerified,
      v.entryVerified,
      v.deliveryTimeVerified,
      v.storeOrderVerified,
      v.storeSpendVerified,
    ];
    
    // Driver check only if driver assigned
    if (hasDriver) requiredChecks.push(v.driverVerified);
    
    // Driver pay check only if pay is set
    if (hasDriverPay) requiredChecks.push(v.driverPayVerified);
    
    return requiredChecks.every(Boolean);
  };

  // Check if order has blocking issues
  const hasBlockingIssues = (order) => {
    return order.needsEntryInstructions || order.needsDeliveryTime;
  };

  // Count approved orders
  const approvedCount = tomorrowOrders.filter(o => o.eodReview?.reviewedAt).length;
  const verifiedCount = tomorrowOrders.filter(o => isOrderFullyVerified(o) && !hasBlockingIssues(o)).length;
  const totalCount = tomorrowOrders.length;

  // Handle approve order
  const handleApprove = async (order) => {
    if (!isOrderFullyVerified(order) || hasBlockingIssues(order)) return;
    
    await onApproveOrder(order._id, {
      reviewedBy: adminInfo?._id,
      reviewedByName: reviewerName,
      reviewedAt: new Date().toISOString(),
      allChecksPass: true,
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80 z-50 overflow-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-white">🌙 EOD Review</h1>
                  <p className="text-violet-200 text-sm">Tomorrow's Orders - {new Date(tomorrowDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                </div>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
                >
                  Exit Review
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FiUser className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Reviewer:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{reviewerName}</span>
                  </div>
                </div>
                
                {/* Progress */}
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Progress: <span className="font-bold text-violet-600 dark:text-violet-400">{approvedCount}</span> of {totalCount} approved
                  </div>
                  <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-violet-500 transition-all duration-300"
                      style={{ width: `${totalCount > 0 ? (approvedCount / totalCount) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Orders List */}
          {tomorrowOrders.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-12 text-center">
              <FiCheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Orders for Tomorrow!</h2>
              <p className="text-gray-600 dark:text-gray-400">There are no orders scheduled for delivery tomorrow.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tomorrowOrders.map((order) => {
                const v = getOrderVerification(order._id);
                const isVerified = isOrderFullyVerified(order);
                const hasBlocking = hasBlockingIssues(order);
                const isApproved = !!order.eodReview?.reviewedAt;
                
                // Check individual field statuses
                const hasStoreOrderId = order.storeOrderId?.trim().length > 0;
                const hasStoreSpend = parseFloat(order.storeSpend) > 0;
                const hasDriver = !!order.driverID;
                const hasEntryInstructions = order.entryInstructions?.trim().length > 0;
                const hasDeliveryTime = !!order.storeArrivalTime;
                const hasDriverPay = parseFloat(order.driverPay) > 0;

                return (
                  <div 
                    key={order._id}
                    className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border-2 ${
                      isApproved ? 'border-emerald-500' : 
                      hasBlocking ? 'border-red-300 dark:border-red-700' : 
                      'border-transparent'
                    }`}
                  >
                    {/* Order Header */}
                    <div className={`px-6 py-4 ${isApproved ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <span className="font-mono font-bold text-lg text-gray-900 dark:text-white">
                            #{order.uniqueId}
                          </span>
                          {order.storeArrivalTime && (
                            <span className="px-3 py-1 bg-gray-200 dark:bg-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200">
                              {new Date(order.storeArrivalTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles' })}
                            </span>
                          )}
                        </div>
                        
                        {isApproved && (
                          <span className="flex items-center gap-2 px-3 py-1 bg-emerald-500 text-white rounded-full text-sm font-medium">
                            <FiCheck className="w-4 h-4" /> Approved
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                        <FiMapPin className="w-4 h-4" />
                        <span>{order.property?.address?.line1}, {order.property?.address?.city}</span>
                      </div>
                    </div>

                    {/* Verification Checklist */}
                    <div className="px-6 py-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                        Verification Checklist
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Pickup Time */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.pickupTimeVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.pickupTimeVerified}
                            onChange={() => toggleVerification(order._id, 'pickupTimeVerified')}
                            disabled={isApproved}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">Pickup time matches grocer</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Verified on grocer website</p>
                          </div>
                        </label>

                        {/* Entry Instructions */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          order.needsEntryInstructions ? 'bg-red-50 dark:bg-red-900/20 cursor-not-allowed' :
                          v.entryVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}>
                          <input
                            type="checkbox"
                            checked={v.entryVerified}
                            onChange={() => toggleVerification(order._id, 'entryVerified')}
                            disabled={isApproved || order.needsEntryInstructions}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className={`font-medium text-sm ${order.needsEntryInstructions ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                              Entry instructions verified
                              {order.needsEntryInstructions && <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">BLOCKED</span>}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {hasEntryInstructions ? 'Has instructions' : 'Missing instructions'}
                            </p>
                          </div>
                        </label>

                        {/* Delivery Time */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          order.needsDeliveryTime ? 'bg-red-50 dark:bg-red-900/20 cursor-not-allowed' :
                          v.deliveryTimeVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}>
                          <input
                            type="checkbox"
                            checked={v.deliveryTimeVerified}
                            onChange={() => toggleVerification(order._id, 'deliveryTimeVerified')}
                            disabled={isApproved || order.needsDeliveryTime}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className={`font-medium text-sm ${order.needsDeliveryTime ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                              Delivery time confirmed
                              {order.needsDeliveryTime && <span className="ml-2 text-xs bg-red-500 text-white px-2 py-0.5 rounded">BLOCKED</span>}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {hasDeliveryTime ? new Date(order.storeArrivalTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Not set'}
                            </p>
                          </div>
                        </label>

                        {/* Driver */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.driverVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.driverVerified}
                            onChange={() => toggleVerification(order._id, 'driverVerified')}
                            disabled={isApproved || !hasDriver}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className={`font-medium text-sm ${hasDriver ? 'text-gray-900 dark:text-white' : 'text-orange-600 dark:text-orange-400'}`}>
                              Driver assigned correctly
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {hasDriver ? order.driverName || 'Driver assigned' : 'No driver assigned'}
                            </p>
                          </div>
                        </label>

                        {/* Store Order ID */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.storeOrderVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.storeOrderVerified}
                            onChange={() => toggleVerification(order._id, 'storeOrderVerified')}
                            disabled={isApproved}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className={`font-medium text-sm ${hasStoreOrderId ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-400'}`}>
                              Store order ID entered
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {hasStoreOrderId ? `#${order.storeOrderId}` : 'Not entered'}
                            </p>
                          </div>
                        </label>

                        {/* Store Spend */}
                        <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.storeSpendVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                          <input
                            type="checkbox"
                            checked={v.storeSpendVerified}
                            onChange={() => toggleVerification(order._id, 'storeSpendVerified')}
                            disabled={isApproved}
                            className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <div>
                            <p className={`font-medium text-sm ${hasStoreSpend ? 'text-gray-900 dark:text-white' : 'text-red-600 dark:text-red-400'}`}>
                              Store spend entered
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {hasStoreSpend ? `$${parseFloat(order.storeSpend).toFixed(2)}` : 'Not entered'}
                            </p>
                          </div>
                        </label>

                        {/* Driver Pay (optional) */}
                        {hasDriverPay && (
                          <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${v.driverPayVerified ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                            <input
                              type="checkbox"
                              checked={v.driverPayVerified}
                              onChange={() => toggleVerification(order._id, 'driverPayVerified')}
                              disabled={isApproved}
                              className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <div>
                              <p className="font-medium text-sm text-gray-900 dark:text-white">Concierge pay set</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                ${parseFloat(order.driverPay).toFixed(2)}
                              </p>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Action Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        {hasBlocking && (
                          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                            <FiAlertCircle className="w-5 h-5" />
                            <span className="font-medium text-sm">Resolve blocking issues before approving</span>
                          </div>
                        )}
                        
                        {!hasBlocking && !isApproved && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {isVerified ? (
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium">✓ All checks complete</span>
                            ) : (
                              <span>Complete all verification checks to approve</span>
                            )}
                          </div>
                        )}
                        
                        {isApproved && order.eodReview && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Approved by {order.eodReview.reviewedByName} at {new Date(order.eodReview.reviewedAt).toLocaleTimeString()}
                          </div>
                        )}

                        <button
                          onClick={() => handleApprove(order)}
                          disabled={!isVerified || hasBlocking || isApproved}
                          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-medium transition-colors ${
                            isApproved ? 'bg-emerald-100 text-emerald-700 cursor-default' :
                            isVerified && !hasBlocking ? 'bg-violet-600 hover:bg-violet-700 text-white' : 
                            'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isApproved ? (
                            <>
                              <FiCheck className="w-5 h-5" />
                              Approved
                            </>
                          ) : (
                            <>
                              <FiCheckCircle className="w-5 h-5" />
                              Approve Order
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Completion Banner */}
          {approvedCount === totalCount && totalCount > 0 && (
            <div className="mt-6 bg-emerald-500 rounded-2xl p-6 text-center text-white">
              <FiCheckCircle className="w-12 h-12 mx-auto mb-3" />
              <h2 className="text-xl font-bold mb-1">All Orders Reviewed!</h2>
              <p className="text-emerald-100">Tomorrow's {totalCount} orders have been approved and are ready for delivery.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

